/**
 * PLAN DOCTOR PICKER -- the PER-LINE treatment-doctor assignment (L-10).
 * NOT WIRED, NOT PRODUCTION.
 *
 * WHY IT EXISTS, AND WHAT IT DOES NOT DO. The reviewer's instruction was that
 * the per-line doctor picker is this lane's component and Lane 1 integrates it.
 * It is therefore an EXTRACTION, not a new control: everything it renders and
 * every rule it applies already ships.
 *
 *   - THE PICKER is `HandoffDoctorPicker` (NextVisitPanel.tsx:419), THE one
 *     doctor picker, which already takes `requiredSpecialties`, `instanceId`
 *     and `fieldLabel` so a second mount cannot collide with the visit-grain
 *     handoff's ids. No second picker is introduced.
 *   - THE SPECIALTY RULE is `requiredSpecialtiesForLine` +
 *     `doctorMeetsProcedureCapability` (doctor-consult-view.ts:577-599),
 *     already shipped and already pinned. No second rule is written here.
 *   - THE WRITE is the caller's existing plan-item update. This component
 *     fires no fetch.
 *
 * What this file adds is the COMPOSITION Lane 1 currently holds inline
 * (ConsultWorkspacePage.tsx:2693): the per-line query state, the derivation of
 * that line's required specialties from the plan-scoped map, and the id/label
 * pair that keeps the per-line mount distinct. Lane 1 mounts one component
 * instead of assembling three things per row.
 *
 * FAIL OPEN, ON PURPOSE, AND IT MATTERS. An UNMAPPED procedure admits every
 * doctor, and a doctor with no recorded specialty is admitted rather than
 * hidden -- the shipped rule.
 *
 * WHICH STATE IS LIVE ON QA IS **UNVERIFIED**. The seed for
 * `clinical.procedure_specialties` exists as a file, and no applied-state
 * ledger entry names it -- but an absent ledger entry is NOT proof that the row
 * is absent on QA, and this agent has no path to that database. So the
 * component is built for BOTH states and its regression pins both: FILTERED
 * when a map arrives on the wire, UNFILTERED when none does. Neither state is
 * assumed to be the deployed one. See the run record's L-10 section for the
 * exact read that would settle it.
 */
import { useState } from 'react';
import type { JSX } from 'react';

import { HandoffDoctorPicker } from '@/dashboard/doctor/NextVisitPanel';
import {
  planItemDoctorLabel,
  requiredSpecialtiesForLine,
} from '@/dashboard/doctor/doctor-consult-view';
import {
  PLAN_DECISION_FAILURE_COPY,
  PLAN_DECISION_LAST_CONFIRMED_PREFIX,
  PLAN_DECISION_UNCONFIRMED_PREFIX,
  type PlanDecisionOutcome,
} from '@/dashboard/doctor/PlanDecisionPicklist';
import type { DoctorConsultContext } from '@/dashboard/api';

export interface PlanDoctorPickerProps {
  /** The plan line this assignment belongs to. */
  readonly itemId: number;
  readonly procedureName: string;
  /** The line's catalog code -- the capability key's preferred half. */
  readonly catalogCode: string | null;
  /** The STORED assignment, off the wire. null = not assigned, and it is
   *  never inferred from the visit's own doctor. */
  readonly performingDoctorId: number | null;
  readonly roster: NonNullable<DoctorConsultContext['clinicDoctors']>;
  readonly visitClinicId: number | null;
  readonly rosterTruncated: boolean;
  /** The consult wire's plan-scoped procedure -> required-specialty map.
   *  undefined / absent = unmapped = unfiltered. */
  readonly procedureSpecialties?: Readonly<Record<string, readonly string[]>>;
  readonly onAssign: (itemId: number, doctorId: number | null) => void;
  readonly busy?: boolean;
  /** MOUNT-TIME IDENTITY, added by #780's integration. The shipped consult row
   *  already stems this picker's ids at `consult-item-doctor-<id>`, and three
   *  suites outside this lane read them (doctor-consult-delta.test.tsx,
   *  doctor-hands-v1-captures, doctor-consult-delta-captures). Renaming them
   *  as a side effect of swapping the BODY of a disclosure would break
   *  surfaces this change does not touch, so the caller supplies the stem and
   *  the component's own default stays for an unmounted preview. */
  readonly instanceId?: string;
}

export default function PlanDoctorPicker({
  itemId,
  procedureName,
  catalogCode,
  performingDoctorId,
  roster,
  visitClinicId,
  rosterTruncated,
  procedureSpecialties,
  onAssign,
  busy = false,
  instanceId,
}: PlanDoctorPickerProps): JSX.Element {
  /** The search query is per-line state, which is the only reason this
   *  composition needs to be a component at all. */
  const [query, setQuery] = useState('');

  const required = requiredSpecialtiesForLine(
    procedureSpecialties,
    catalogCode,
    procedureName,
  );
  const current = roster.find((d) => d.id === performingDoctorId) ?? null;

  return (
    <HandoffDoctorPicker
      clinicDoctors={roster}
      visitClinicId={visitClinicId}
      rosterTruncated={rosterTruncated}
      currentDoctorId={performingDoctorId}
      currentDoctorName={current?.name ?? null}
      busy={busy}
      query={query}
      onQueryChange={setQuery}
      onPickDoctor={(doctorId) => onAssign(itemId, doctorId)}
      /* A SECOND MOUNT OF THE ONE PICKER: the per-line stem keeps its ids off
         the visit-grain handoff's, which is exactly what `instanceId` exists
         for. */
      instanceId={instanceId ?? `plan-doctor-${itemId}`}
      fieldLabel="Performing doctor"
      placeholderLabel="Not assigned"
      searchAriaLabel={`Search doctors for ${procedureName}`}
      truncatedTestId={`${instanceId ?? `plan-doctor-${itemId}`}-roster-truncated`}
      requiredSpecialties={required}
    />
  );
}

/**
 * THE ASSIGNMENT'S OUTCOME LINE. SEPARATE from the picker, and that separation
 * is the whole point.
 *
 * WHY IT EXISTS AT ALL. The per-line doctor sits behind the row's "Assign
 * doctor" disclosure. A failure rendered INSIDE that disclosure is invisible
 * the moment the doctor closes it -- which is the same defect as a silent save,
 * and the one the decision and the remark already guard against. The picker
 * therefore renders no failure of its own; the caller places this beside the
 * disclosure, never within it.
 *
 * IT IS THE SAME VOCABULARY, NOT A THIRD ONE. The four codes, the fixed
 * sentence per code, the "Not confirmed" opener and the "Last confirmed"
 * fallback all come from `PlanDecisionPicklist`. A plan line's three writes go
 * through one endpoint and are read by one doctor; a second wording for this
 * one would be a second vocabulary for one fact.
 *
 * THE NAME COMES FROM THE ONE SHIPPED LABEL FUNCTION. `planItemDoctorLabel`
 * (doctor-consult-view.ts:811) already answers "who is assigned?" for a roster
 * and an id -- including the ruled unassigned copy and the `Doctor #id`
 * fallback for an id the roster no longer resolves. A local `.find()?.name`
 * would be a second answer to a question the product already answers.
 */
export function PlanDoctorOutcome({
  itemId,
  performingDoctorId,
  roster,
  outcome,
}: {
  readonly itemId: number;
  /** The LAST CONFIRMED assignment -- never the one just requested. */
  readonly performingDoctorId: number | null;
  readonly roster: NonNullable<DoctorConsultContext['clinicDoctors']>;
  readonly outcome: PlanDecisionOutcome;
}): JSX.Element | null {
  if (outcome.kind !== 'failed') return null;
  return (
    <p
      role="alert"
      data-testid={`plan-doctor-${itemId}-outcome`}
      data-failure={outcome.failure}
    >
      <strong>{PLAN_DECISION_UNCONFIRMED_PREFIX}.</strong>{' '}
      {PLAN_DECISION_FAILURE_COPY[outcome.failure]}{' '}
      {PLAN_DECISION_LAST_CONFIRMED_PREFIX} doctor:{' '}
      {planItemDoctorLabel(performingDoctorId, roster)}.
    </p>
  );
}
